import { BuilderContent } from "@builder.io/sdk";
import React from "react";

import { chunk } from "@/utils/array";
import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";
import { fetchAllRetailerPages } from "@/pages/api/store-directory/stores";

import { RetailerPagesList } from "@/components/retailer-pages-list/retailer-pages-list";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";
import { RetailerPageProps } from "@/interfaces/retailer-page-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

type LocaleProps = {
    locale: Locale;
};

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type GroupedRetailerProps = {
    groupedStores: Array<Array<RetailerPageProps>>;
};

type OwnProps = {
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & GroupedRetailerProps & LocaleProps;

const groupStores = (stores: Array<RetailerPageProps>) => chunk(stores, 10);

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, resolvedUrl }) => {
    const { stores, ...parallelProps } = await parallel({
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        stores: () => fetchAllRetailerPages(locale),
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    const groupedStores = groupStores(stores);

    return {
        props: {
            groupedStores,
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const PageContent = ({
    builderMessages,
    builderNavLinks,
    groupedStores,
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
}: Props): React.ReactNode => {
    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={builderNavLinks}
            builderMessages={builderMessages}
        >
            <RetailerPagesList groupedStores={groupedStores} />
        </DefaultLayout>
    );
};

export default PageContent;
